/**
 * Copying a work order, a material order or the other expenses from one
 * estimate onto another.
 *
 * Jonathon, 2026-08-24: "there should be a copy work order from other estimate
 * button beside the work order. same for material order and other expenses."
 *
 * Pricing one roof three ways means building the same order three times. The
 * mirror (`mirror.ts`) already keeps three estimates in step *while they are
 * being edited*; this is the other half of the same problem — the second
 * estimate that starts from nothing and should start from the first one.
 *
 * ## What is copied, and what is deliberately not
 *
 * A copy carries **what the order is**: who is doing the work, where the
 * material is coming from, the trade, the tax rate, the words somebody typed on
 * it, and every priced line.
 *
 * A copy carries none of **what has happened to it**. Status goes back to the
 * beginning, every timestamp is dropped, the reconciliation is dropped, and the
 * payout approval is dropped. This is the half that would do real damage if it
 * were left in: a material order copied as `paid` is money the ladder believes
 * has left the company, and a work order copied with `payout_approved_at` set
 * is a crew payout approved by somebody who never looked at it. The order
 * number is not copied either — the database assigns it, and two orders sharing
 * one is a conversation with a supplier that cannot be untangled.
 *
 * Change-order lines are not copied. A change order is something *this*
 * customer agreed to on *this* estimate after it was priced; copying it onto a
 * competing option would put an extra on a quote nobody has been shown.
 *
 * ## The column each table actually has
 *
 * A line is called `name` on `material_order_items` and `description` on
 * `work_order_items`, and neither table has the other column. The shapes below
 * declare one each for that reason: writing both, which this did until
 * 2026-09-12, is a PostgREST error that no test and no type ever saw.
 *
 * Nothing here talks to a database — it returns the values to insert, so the
 * awkward part is testable. `apps/web` writes them.
 */

import type { NumericFromDb } from "./types.js";

/** Where the copy is going. The same three facts for all three buckets. */
export interface CopyTarget {
  jobId: string;
  /** The estimate being copied *onto*. */
  estimateId: string;
  /** `profiles.id` of whoever pressed the button. */
  createdBy?: string | null;
}

/** Values to insert, as the web app's `insertRow` takes them. */
export type InsertValues = Record<string, unknown>;

export interface CopiedDocument {
  /** The order row. Insert this first — the items need its id. */
  order: InsertValues;
  /**
   * The item rows, without their foreign key. The caller adds
   * `material_order_id` or `work_order_id` once the order above exists.
   */
  items: InsertValues[];
}

/**
 * The source shapes, kept as narrow as the copy actually needs.
 *
 * Deliberately not `MaterialOrder`/`WorkOrder`: those carry the id, the order
 * number and the timestamps this must never read, and a type that cannot see
 * them is a type that cannot copy them by accident.
 */
export interface CopyableMaterialOrder {
  supplier_id?: string | null;
  supplier_location?: string | null;
  trade?: string | null;
  tax_rate?: NumericFromDb | null;
  suite?: string | null;
  notes?: string | null;
  delivery_note?: string | null;
  pickup?: boolean | null;
  /** The recipe the order was generated from. See ADR 0156. */
  roof_template_id?: string | null;
  generation_answers?: unknown;
  items?: readonly CopyableMaterialItem[] | null;
}

export interface CopyableMaterialItem {
  product_id?: string | null;
  /** `material_order_items` has `name` and no `description`. */
  name?: string | null;
  qty?: NumericFromDb | null;
  unit?: string | null;
  unit_price?: NumericFromDb | null;
  calc_note?: string | null;
  color?: string | null;
  sort_order?: number | null;
}

export interface CopyableWorkOrder {
  crew_id?: string | null;
  field_contact_id?: string | null;
  from_company?: string | null;
  trades?: string[] | string | null;
  tax_rate?: NumericFromDb | null;
  suite?: string | null;
  description?: string | null;
  office_notes?: string | null;
  attach_material_list?: string | null;
  items?: readonly CopyableWorkItem[] | null;
}

export interface CopyableWorkItem {
  product_id?: string | null;
  /** `work_order_items` has `description` and no `name`. */
  description?: string | null;
  qty?: NumericFromDb | null;
  unit?: string | null;
  unit_price?: NumericFromDb | null;
  calc_note?: string | null;
  sort_order?: number | null;
  is_change_order?: boolean | null;
}

export interface CopyableExpense {
  name?: string | null;
  amount?: NumericFromDb | null;
  payment_type?: string | null;
  trade?: string | null;
  tags?: string[] | null;
  note?: string | null;
  sort_order?: number | null;
  is_change_order?: boolean | null;
}

/**
 * The status a copied order starts at.
 *
 * Named here rather than imported from `orderStatus.ts` so that this file
 * states its own rule: a copy has not been placed and has not been scheduled,
 * whatever the order it came from had got to.
 */
const MATERIAL_START = "not_placed";
const WORK_START = "not_scheduled";

/** Line ordering, renumbered from the top so a copy reads in the same order. */
const renumber = (index: number): number => (index + 1) * 10;

/**
 * Lines in the order they were written, change orders left behind.
 *
 * Sorted here rather than trusted from the query, because the renumbering below
 * is what fixes the order for good and doing it to rows in arrival order would
 * quietly reshuffle a material order that a supplier reads top to bottom.
 */
function priced<
  T extends { sort_order?: number | null; is_change_order?: boolean | null },
>(items: readonly T[] | null | undefined): T[] {
  return [...(items ?? [])]
    .filter((item) => item.is_change_order !== true)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
}

/** A material order and its lines, ready to insert onto another estimate. */
export function copyMaterialOrder(
  source: CopyableMaterialOrder,
  target: CopyTarget,
): CopiedDocument {
  return {
    order: {
      job_id: target.jobId,
      estimate_id: target.estimateId,
      supplier_id: source.supplier_id ?? null,
      supplier_location: source.supplier_location ?? null,
      trade: source.trade ?? null,
      // The rate is a fact about the supplier, not about progress, so it comes
      // across. Getting this wrong is a wrong cost on the ladder.
      tax_rate: source.tax_rate ?? 0,
      suite: source.suite ?? null,
      notes: source.notes ?? null,
      delivery_note: source.delivery_note ?? null,
      pickup: source.pickup ?? null,
      // A copy is the same recipe on another option, so it keeps the record of
      // which template and answers produced it. Not `quote_checked_at`: a check
      // against one estimate says nothing about the estimate it is copied onto.
      roof_template_id: source.roof_template_id ?? null,
      generation_answers: source.generation_answers ?? null,
      status: MATERIAL_START,
      created_by: target.createdBy ?? null,
    },
    items: priced(source.items).map((item, index) => ({
      product_id: item.product_id ?? null,
      name: item.name ?? null,
      qty: item.qty ?? 0,
      unit: item.unit ?? null,
      unit_price: item.unit_price ?? 0,
      // Kept as it was written. It says what the *original* order was priced
      // off — "Valley = 30 LF" — and a copy of that line was priced off the
      // same thing. Recomputing it here would be inventing a measurement.
      calc_note: item.calc_note ?? null,
      color: item.color ?? null,
      sort_order: renumber(index),
    })),
  };
}

/** A work order and its lines, ready to insert onto another estimate. */
export function copyWorkOrder(
  source: CopyableWorkOrder,
  target: CopyTarget,
): CopiedDocument {
  return {
    order: {
      job_id: target.jobId,
      estimate_id: target.estimateId,
      // The crew comes across, because a crew is not a label: one we carry
      // workers comp for is charged at a percentage of the work order, and a
      // copy that dropped the crew would quietly drop that cost too.
      crew_id: source.crew_id ?? null,
      field_contact_id: source.field_contact_id ?? null,
      from_company: source.from_company ?? null,
      trades: source.trades ?? null,
      tax_rate: source.tax_rate ?? 0,
      suite: source.suite ?? null,
      description: source.description ?? null,
      office_notes: source.office_notes ?? null,
      attach_material_list: source.attach_material_list ?? null,
      status: WORK_START,
      created_by: target.createdBy ?? null,
    },
    items: priced(source.items).map((item, index) => ({
      product_id: item.product_id ?? null,
      description: item.description ?? null,
      qty: item.qty ?? 0,
      unit: item.unit ?? null,
      unit_price: item.unit_price ?? 0,
      calc_note: item.calc_note ?? null,
      sort_order: renumber(index),
    })),
  };
}

/**
 * The other expenses, ready to insert onto another estimate.
 *
 * A flat list rather than a document, because expenses have no header row.
 * `document_url` and `tracking_number` are not copied: a receipt and an invoice
 * number belong to the money that was actually spent, and attaching them to a
 * second estimate's copy would file one receipt against two costs.
 */
export function copyExpenses(
  rows: readonly CopyableExpense[],
  target: CopyTarget,
): InsertValues[] {
  return priced(rows).map((row, index) => ({
    job_id: target.jobId,
    estimate_id: target.estimateId,
    name: row.name ?? null,
    amount: row.amount ?? 0,
    payment_type: row.payment_type ?? null,
    trade: row.trade ?? null,
    tags: row.tags ?? null,
    note: row.note ?? null,
    sort_order: renumber(index),
  }));
}

/**
 * Every key a copy must never carry, for the test that guards this file.
 *
 * The failure this is aimed at is additive: somebody adds `paid_at` to the copy
 * because they were copying "everything about the order", and the ladder starts
 * counting a cost that was never incurred. Listing them here means the test
 * fails on the day it is added rather than on the day somebody reconciles.
 */
export const NEVER_COPIED: readonly string[] = [
  "id",
  "order_number",
  "created_at",
  "reconcile_amt",
  "reconciled_at",
  "quote_checked_at",
  "document_filename",
  "document_url",
  "tracking_number",
  "is_change_order",
  "bill_date",
  "due_date",
  "delivery_date",
  "start_at",
  "end_at",
  "scheduled_at",
  "completed_at",
  "ordered_at",
  "picked_up_at",
  "paid_at",
  "payout_approved_at",
  "payout_approved_by",
  "payout_paid_manually_at",
  "payout_paid_manually_by",
  "payout_paid_at",
  "payout_paid_by",
  "occurred_at",
  "hold_order",
];
